import { useEffect, useState } from 'react';
import { CA_PLANS_MAPPED, CA_PLANS_PLAN_SPECIFIC } from '@/components/marketing/siteFacts';
import { getWeaveinApiUrl } from '@/src/config';

/**
 * Funder coverage, read from the platform rather than typed into the page.
 *
 * GET /api/public/funder-coverage returns aggregates only — counts, states and
 * channel statuses, never an organization. The fallback is what is already true
 * without the API: the California plans with rules held as data, and no
 * directory count at all. A page never shows a national number it did not read.
 */

export type ChannelStatus = 'live' | 'pilot' | 'planned';

export interface CoverageChannel {
  key: string;
  label: string;
  status: ChannelStatus;
}

export interface PlanLayers {
  seq: number;
  state: string;
  has_edi: boolean;
  /** Filing window, channel and national add-on codes — copied onto every plan. */
  generic: number;
  /** One state's benefit rules applied to every plan in it. */
  statewide: number;
  /** Rules (and Community Supports menu rows) that belong to this plan alone. */
  plan_specific: number;
  /** Agent-drafted rules for this plan that a person has not approved yet. */
  drafted?: number;
  rule_rows: number;
}

export interface FunderCoverage {
  as_of: string | null;
  mapped_plans: {
    count: number;
    states: string[];
    with_plan_rules: number;
    /** Plans with agent drafts awaiting approval and no approved rules of their own yet. */
    with_drafts?: number;
    /** The headline: plans with rules of their own, approved or drafted. */
    with_plan_rules_or_drafts?: number;
    rule_rows: number;
  };
  directory: {
    payers: number;
    states: number;
    medicaid: number;
    medicare: number;
    eligibility: number;
    claims: number;
    remittance: number;
    last_synced_at: string | null;
  };
  channels: CoverageChannel[];
  clearinghouses: CoverageChannel[];
  rule_proposals: { pending: number; approved: number };
  /** One row per mapped plan, anonymised, in the order its rules arrived (BE #1332). */
  build?: PlanLayers[];
  /** Medicaid payers in the national directory with no rules held yet. */
  queue?: { medicaid_without_rules: number; by_state: { state: string; count: number }[] };
  /** TRUS-776: what the opportunity finder scans beyond the payer directory. */
  opportunity_sources?: { programs: number; sponsors: number };
  /**
   * TRUS-778: the source finder's search for each plan's own documents (provider
   * manual, billing guide), which the rules builder drafts from. Older backends omit it.
   */
  discovery?: {
    plans_searched: number;
    documents_found: number;
    documents_in_review: number;
    documents_registered: number;
    plans_with_documents: number;
    last_searched_at: string | null;
  };
}

export const FALLBACK_COVERAGE: FunderCoverage = {
  as_of: null,
  mapped_plans: { count: CA_PLANS_MAPPED, states: ['CA'], with_plan_rules: CA_PLANS_PLAN_SPECIFIC, rule_rows: 0 },
  directory: {
    payers: 0,
    states: 0,
    medicaid: 0,
    medicare: 0,
    eligibility: 0,
    claims: 0,
    remittance: 0,
    last_synced_at: null,
  },
  // Mirrors _CHANNELS in the backend's public_funder_coverage.py, so the page
  // reads the same before and after the API answers. Stedi and Availity are
  // always shown live (product decision 2026-11-02), so the 837 channel is too.
  channels: [
    { key: 'clearinghouse_837', label: '837 claims through a clearinghouse', status: 'live' },
    { key: 'portal', label: 'Plan portal upload, tracked', status: 'live' },
    { key: 'workbook', label: 'Plan invoice workbook', status: 'pilot' },
    { key: 'paper', label: 'Paper, tracked', status: 'live' },
    // Tracked like portal and paper: routed and recorded, completed by a person (BE #1334).
    { key: 'sftp', label: 'SFTP file drop, tracked', status: 'live' },
    { key: 'secure_email', label: 'Secure email, tracked', status: 'live' },
  ],
  clearinghouses: [
    { key: 'stedi', label: 'Stedi (national payer network)', status: 'live' },
    { key: 'availity', label: 'Availity', status: 'live' },
  ],
  rule_proposals: { pending: 0, approved: 0 },
};

/** Plans with rules of their own, counting agent drafts not yet approved (older backends: approved only). */
export const plansWithOwnRules = (mp: FunderCoverage['mapped_plans']) => mp.with_plan_rules_or_drafts ?? mp.with_plan_rules;

export default function useFunderCoverage() {
  const [coverage, setCoverage] = useState<FunderCoverage>(FALLBACK_COVERAGE);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // getWeaveinApiUrl, not a relative path: on weavein.ai a relative /api/
    // path is rewritten to the development backend (see contact/page.tsx).
    fetch(getWeaveinApiUrl('/api/public/funder-coverage'))
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: FunderCoverage) => {
        if (cancelled || !data?.mapped_plans) return;
        setCoverage({ ...FALLBACK_COVERAGE, ...data });
        setLive(true);
      })
      .catch(() => {
        /* the fallback is already on screen */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { coverage, live };
}
